import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, ELinxHistoryItem, withStyles, GetColorStatus, capitalizeFirstLetterOfWords, PaymentOption } from 'vopay';
import { CopyButton, TransactionStatus, Button, Truncate } from 'UI';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import Complete from '@/components/UI/Complete/Complete';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import styles from './ElinxConfirmationPage.module.scss';

const cn = withStyles(styles);

type ElinxType = 'pay' | 'collect' | 'connect' | 'flex';
type NotificationMethod = 'email' | 'sms' | 'none';

const ElinxConfirmationPage: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { eLinxId, type, notificationMethod, eFlexOption } = useParams<{
    eLinxId: string;
    type: ElinxType;
    notificationMethod: NotificationMethod;
    eFlexOption: PaymentOption | string;
  }>();
  const [eLinx, setELinx] = useState<ELinxHistoryItem>();
  const [eLixResponse, , , fetchElinxHistory] = useFetchELinxHistory({});

  // Parse query params
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isScheduled = queryParams.get('scheduled') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchElinxHistory({ ELinxRequestID: eLinxId });

        if (!res.Success) {
          throw new Error(res.ErrorMessage);
        }
        setELinx(res.ELinxRequests[0]);
      } catch (error) {
        console.error(error);
        alertDialog.error(
          t('ErrorMessages.error', { defaultValue: 'Error' }),
          t('ErrorMessages.fetchingError', { defaultValue: 'An error occurred while fetching eLinx history' }),
        );
      }
    };

    fetchData();
  }, [eLinxId]);

  const eFlexOptionMap: Record<PaymentOption, string> = {
    multiple: t('Confirmation.multiple', { defaultValue: 'Multiple Payment Methods' }),
    partial: t('Confirmation.partial', { defaultValue: 'Partial Payment' }),
    installments: t('Confirmation.installments', { defaultValue: 'Installment Plan' }),
  };

  const formattedEflexOptions = eFlexOption
    .split(',')
    .filter((opt) => opt === 'multiple' || opt === 'partial' || opt === 'installments')
    .map((opt) => eFlexOptionMap[opt])
    .join(', ');

  const notificationMethodMap: Record<NotificationMethod, string> = {
    sms: t('Confirmation.sms', { defaultValue: 'SMS' }),
    email: t('Confirmation.email', { defaultValue: 'Email' }),
    none: '',
  };

  const elinxTypeMap: Record<ElinxType, string> = {
    pay: t('Type.ePay', { defaultValue: 'ePay' }),
    collect: t('Type.eCollect', { defaultValue: 'eCollect' }),
    connect: t('Type.eConnect', { defaultValue: 'eConnect' }),
    flex: t('Type.eFlex', { defaultValue: 'eFlex' }),
  };

  const descriptionText = useMemo(() => {
    if (type === 'connect') return null;
    if (type === 'pay') {
      return t('Confirmation.paymentProcessingDescription', {
        defaultValue: 'Payment will start processing after this contact provides their preferred payment method.',
      });
    }
    if (type === 'collect' || type === 'flex') {
      return t('Confirmation.collectionProcessingDescription', {
        defaultValue: 'Collection will start processing after this contact provides their preferred payment method.',
      });
    }
    return null;
  }, [type, t]);

  const title = useMemo(() => {
    const typeLabel = elinxTypeMap[type];
    return t('Confirmation.title', { type: typeLabel, defaultValue: `${typeLabel} Request Submitted` });
  }, [type, t, elinxTypeMap]);

  const actionText = useMemo(() => {
    if (isScheduled) {
      return t('Confirmation.upcomingAmount', { defaultValue: 'Upcoming Amount' });
    }
    return type === 'pay'
      ? t('Confirmation.paymentAmount', { defaultValue: 'Payment Amount' })
      : t('Confirmation.collectionAmount', { defaultValue: 'Collection Amount' });
  }, [isScheduled, type, t]);

  const recipientText = useMemo(() => {
    if (type === 'connect') return null;

    let translationKey: string;
    let defaultValue: string;

    if (isScheduled) {
      translationKey = type === 'pay' ? 'ElinxHistory.Confirmation.to' : 'ElinxHistory.Confirmation.from';
      defaultValue = type === 'pay' ? 'to <bold>{{name}}</bold>' : 'from <bold>{{name}}</bold>';
    } else {
      translationKey = type === 'pay' ? 'ElinxHistory.Confirmation.payableTo' : 'ElinxHistory.Confirmation.collectFrom';
      defaultValue = type === 'pay' ? 'payable to <bold>{{name}}</bold>' : 'collect from <bold>{{name}}</bold>';
    }

    return (
      <Trans
        ns="translations"
        i18nKey={translationKey}
        defaults={defaultValue}
        values={{ name: eLinx?.ReceiverName }}
        components={{ bold: <strong style={{ color: '#2d2d2d' }} /> }}
      />
    );
  }, [isScheduled, type, eLinx?.ReceiverName, t]);

  const details = useMemo(() => {
    const baseDetails = [
      {
        label: t('Confirmation.elinxLink', { defaultValue: 'eLinx Link' }),
        value: (
          <Box gapSize="8px" ai="center" jc="fe">
            <span>
              <Truncate text={eLinx?.Link || ''} maxLength={25} truncateEnding="..." />
            </span>
            <CopyButton content={eLinx?.Link || ''} />
          </Box>
        ),
      },
      {
        label: t('Confirmation.elinxId', { defaultValue: 'eLinx ID' }),
        value: (
          <Box gapSize="8px" ai="center" jc="fe">
            <span>{eLinx?.ELinxRequestID}</span>
            <CopyButton content={eLinx?.ELinxRequestID || ''} />
          </Box>
        ),
      },
      {
        label: t('Confirmation.status', { defaultValue: 'Status' }),
        value: (
          <Box jc="fe">
            <TransactionStatus
              status={GetColorStatus(eLinx?.Status)}
              label={tGlobal(`Statuses.${eLinx?.Status?.toLowerCase()}`, {
                defaultValue: capitalizeFirstLetterOfWords(eLinx?.Status || ''),
              })}
            />
          </Box>
        ),
      },
      ...(notificationMethod !== 'none'
        ? [
            {
              label: t('Confirmation.notification', { defaultValue: 'Send Notifications via' }),
              value: notificationMethodMap[notificationMethod],
            },
          ]
        : []),
      ...(eFlexOption !== 'none' && formattedEflexOptions
        ? [
            {
              label: t('Confirmation.eflexOptions', { defaultValue: 'eFlex Options' }),
              value: formattedEflexOptions,
            },
          ]
        : []),
    ];

    return baseDetails;
  }, [eLinx, notificationMethod, notificationMethodMap, eFlexOption, formattedEflexOptions, t, tGlobal]);

  const buttonLabel = useMemo(() => {
    return t('Confirmation.viewDetails', {
      type: elinxTypeMap[type],
      defaultValue: `View ${elinxTypeMap[type]} Details`,
    });
  }, [type, elinxTypeMap, t]);

  const createNewLabel = useMemo(() => {
    return t('Confirmation.createNew', { type: elinxTypeMap[type], defaultValue: `Create New ${elinxTypeMap[type]}` });
  }, [type, elinxTypeMap, t]);

  const handleViewDetails = () => {
    history.replace(`/elinx-history/details/${eLinx?.ELinxRequestID || ''}`);
  };

  const handleCreateNew = () => {
    history.replace(`/elinx/${type}`);
  };

  if (!eLixResponse.Success || !eLinx) {
    return null;
  }

  // Description to show under the title
  const description =
    type === 'connect' ? (
      <div className={cn('message')}>
        <Trans
          ns="translations"
          i18nKey="ElinxHistory.Confirmation.connectSummary"
          values={{ receiverName: eLinx?.ReceiverName }}
          components={{
            span: <span style={{ fontWeight: 600, color: '#2D2D2D' }} />,
          }}
        />
      </div>
    ) : descriptionText ? (
      descriptionText
    ) : null;

  return (
    <Complete
      title={title}
      amount={type !== 'connect' ? `${eLinx?.Currency} ${eLinx?.Amount}` : undefined}
      actionText={type !== 'connect' ? actionText : undefined}
      recipientText={type !== 'connect' ? recipientText : undefined}
      showAmounts={type !== 'connect'}
      details={details}
      description={description}
      className={cn('container')}
    >
      <div className={cn('buttonContainer')}>
        <Button size="small" onClick={handleViewDetails}>
          {buttonLabel}
        </Button>
        <Button size="small" colorScheme="primary" onClick={handleCreateNew}>
          {createNewLabel}
        </Button>
      </div>
    </Complete>
  );
};

export default ElinxConfirmationPage;
